/**
 * Live map: the single derivation of what a technician is doing and where to draw them, from
 * their get_fleet_live() row (migration 20261019130000) and their jobs today. The Live map, its
 * side panel and the Schedule map all read it, so they never disagree.
 *
 * Positions are coarse by design (owner decision 2026-10-19, the Housecall Pro model): Android
 * reports about every 15 minutes and iOS only after ~400 m of movement, so an iPhone parked at a
 * job sends nothing for hours. Job progress (on my way / on site) is exact and always wins over
 * the dot. No path aliases here, so the helpers can be unit-tested outside Vite.
 */
import { enRouteEta, type EnRouteEta, type TechProgressSource } from './techProgress';

export type IsoWeekday = '1' | '2' | '3' | '4' | '5' | '6' | '7';
/** {"1": ["07:00","18:00"], …, "7": null}: ISO weekday (1 = Monday), company timezone. */
export type TrackingHours = Partial<Record<IsoWeekday, [string, string] | null>>;

export const ISO_WEEKDAYS: IsoWeekday[] = ['1', '2', '3', '4', '5', '6', '7'];
export const WEEKDAY_SHORT: Record<IsoWeekday, string> = {
  '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun',
};
export const DEFAULT_TRACKING_HOURS: TrackingHours = {
  '1': ['07:00', '18:00'], '2': ['07:00', '18:00'], '3': ['07:00', '18:00'],
  '4': ['07:00', '18:00'], '5': ['07:00', '18:00'], '6': null, '7': null,
};

/** One technician as get_fleet_live() returns it (null keys are stripped server-side). */
export interface FleetTech {
  tech_id: string;
  name: string;
  phone?: string | null;
  tracking_enabled: boolean;
  notice_accepted: boolean;
  notice_accepted_at?: string | null;
  permission?: 'always' | 'while_using' | 'denied' | 'services_off' | 'unknown' | null;
  battery_pct?: number | null;
  platform?: 'ios' | 'android' | 'other' | null;
  app_version?: string | null;
  reported_at?: string | null;
  tracking_active?: boolean | null;
  lat?: number | null;
  lng?: number | null;
  accuracy_m?: number | null;
  speed_mps?: number | null;
  heading?: number | null;
  fix_at?: string | null;
}

export interface FleetSnapshot {
  tracking_enabled: boolean;
  notice_confirmed: boolean;
  timezone: string;
  hours: TrackingHours;
  in_hours_now: boolean;
  history_days: number;
  notice_version: number;
  techs: FleetTech[];
}

/** The job fields the derivation needs (ScheduleJob satisfies it). */
export interface FleetJob extends TechProgressSource {
  id: string;
  job_number: string | null;
  status: string;
  assigned_tech_id: string | null;
  lat: number | null;
  lng: number | null;
  scheduled_time_start?: string | null;
}

/** No report at all for longer than this during hours = "Not reporting" (iOS is quiet when parked). */
export const NOT_REPORTING_MINUTES: Record<'ios' | 'android' | 'other', number> = { ios: 120, android: 45, other: 45 };
/** A fix older than this is drawn faded; an on-site job location is preferred over it. */
export const FRESH_FIX_MINUTES = 30;

export type FleetActivity = 'on_site' | 'en_route' | 'between_jobs' | 'done_for_day' | 'no_jobs';
export type FleetLocationState =
  | 'live'
  | 'not_reporting'
  | 'location_off'
  | 'off_hours'
  | 'notice_pending'
  | 'exempt'
  | 'tracking_off'
  | 'no_reports';

export interface FleetPosition {
  lat: number;
  lng: number;
  accuracyM: number | null;
  /** 'fix' = the phone's position; 'job' = the job the tech pressed Start at (no fresh fix). */
  source: 'fix' | 'job';
  ageMinutes: number | null;
}

export interface FleetStatus {
  activity: FleetActivity;
  activityLabel: string;
  /** The job the tech is on the way to, or on site at. */
  activeJob: FleetJob | null;
  /** The next open job today that has not started. */
  nextJob: FleetJob | null;
  eta: EnRouteEta | null;
  location: FleetLocationState;
  locationLabel: string;
  /** Badge/ring tone from the design tokens. */
  tone: 'success' | 'warning' | 'info' | 'muted';
  position: FleetPosition | null;
  /** Drawn from a fix older than FRESH_FIX_MINUTES. */
  stale: boolean;
}

const CLOSED = new Set(['completed', 'cancelled']);
const IDLE_ON_JOB = new Set(['on_hold', 'quote_sent']);
const WEEKDAY_TO_ISO: Record<string, IsoWeekday> = {
  Mon: '1', Tue: '2', Wed: '3', Thu: '4', Fri: '5', Sat: '6', Sun: '7',
};

/** Local weekday and "HH:mm" of `at` in `timezone` (falls back to Sydney on a bad zone). */
export function localClock(timezone: string, at: Date = new Date()): { weekday: IsoWeekday; time: string } {
  const read = (tz: string) =>
    new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(at);
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = read(timezone || 'Australia/Sydney');
  } catch {
    parts = read('Australia/Sydney');
  }
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return { weekday: WEEKDAY_TO_ISO[get('weekday')] ?? '1', time: `${get('hour')}:${get('minute')}` };
}

/** Mirrors location_in_hours() in SQL: start inclusive, end exclusive, null day = no tracking. */
export function inTrackingHours(hours: TrackingHours | null | undefined, timezone: string, at: Date = new Date()): boolean {
  if (!hours) return false;
  const { weekday, time } = localClock(timezone, at);
  const window = hours[weekday];
  return !!window && time >= window[0] && time < window[1];
}

export function minutesSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / 60_000));
}

/** "just now", "6 min ago", "2 h ago". */
export function formatAge(minutes: number | null): string {
  if (minutes == null) return 'never';
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const h = Math.floor(minutes / 60);
  return h < 48 ? `${h} h ago` : `${Math.floor(h / 24)} days ago`;
}

/** Great-circle distance in km. */
export function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(s)));
}

export function formatDistance(km: number, unit: 'km' | 'mi'): string {
  const value = unit === 'mi' ? km * 0.621371 : km;
  const tenths = Math.round(value * 10) / 10;
  return `${tenths < 10 ? tenths.toFixed(1) : Math.round(value)} ${unit}`;
}

function hasCoords(job: FleetJob | null | undefined): job is FleetJob & { lat: number; lng: number } {
  return !!job && job.lat != null && job.lng != null;
}

function byStart(a: FleetJob, b: FleetJob): number {
  return (a.scheduled_time_start ?? '99:99').localeCompare(b.scheduled_time_start ?? '99:99');
}

export function deriveFleetStatus(
  tech: FleetTech,
  jobsToday: FleetJob[],
  fleet: Pick<FleetSnapshot, 'tracking_enabled' | 'hours' | 'timezone'>,
  now: Date = new Date(),
): FleetStatus {
  // ── What they're doing (exact: job progress from the app) ──────────────────
  const open = jobsToday.filter((j) => !CLOSED.has(j.status));
  const onSite = open.find((j) => j.arrived_at && !j.work_finished_at && !IDLE_ON_JOB.has(j.status)) ?? null;
  const enRoute = onSite
    ? null
    : open
        .filter((j) => j.en_route_at && !j.arrived_at && !j.work_finished_at)
        .sort((a, b) => (b.en_route_at ?? '').localeCompare(a.en_route_at ?? ''))[0] ?? null;
  const nextJob = open.filter((j) => !j.en_route_at && !j.arrived_at && j !== onSite).sort(byStart)[0] ?? null;
  const activeJob = onSite ?? enRoute;
  const eta = enRoute ? enRouteEta(enRoute, now) : null;

  let activity: FleetActivity;
  let activityLabel: string;
  if (onSite) {
    activity = 'on_site';
    activityLabel = `On site · ${onSite.job_number ?? 'job'}`;
  } else if (enRoute) {
    activity = 'en_route';
    activityLabel = `On the way · ${enRoute.job_number ?? 'job'}`;
  } else if (open.length > 0) {
    activity = 'between_jobs';
    activityLabel = 'Between jobs';
  } else if (jobsToday.length > 0) {
    activity = 'done_for_day';
    activityLabel = 'Done for the day';
  } else {
    activity = 'no_jobs';
    activityLabel = 'No jobs today';
  }

  // ── Where the phone is (coarse, may be quiet) ─────────────────────────────
  const reportAge = minutesSince(tech.reported_at, now);
  const fixAge = minutesSince(tech.fix_at, now);
  const limit = NOT_REPORTING_MINUTES[tech.platform ?? 'other'] ?? NOT_REPORTING_MINUTES.other;

  let location: FleetLocationState;
  let locationLabel: string;
  if (!fleet.tracking_enabled) {
    location = 'tracking_off';
    locationLabel = 'Tracking is off';
  } else if (!tech.tracking_enabled) {
    location = 'exempt';
    locationLabel = 'Not tracked';
  } else if (!tech.notice_accepted) {
    location = 'notice_pending';
    locationLabel = 'Hasn’t accepted tracking yet';
  } else if (!inTrackingHours(fleet.hours, fleet.timezone, now)) {
    location = 'off_hours';
    locationLabel = 'Outside tracking hours';
  } else if (tech.permission === 'denied' || tech.permission === 'services_off') {
    location = 'location_off';
    locationLabel = 'Location off on phone';
  } else if (reportAge == null) {
    location = 'no_reports';
    locationLabel = 'No reports from the app yet';
  } else if (reportAge > limit) {
    location = 'not_reporting';
    locationLabel = `Not reporting · last ${formatAge(reportAge)}`;
  } else {
    location = 'live';
    locationLabel = fixAge == null ? 'No position yet today' : `Seen ${formatAge(fixAge)}`;
  }

  const mayDraw = location === 'live' || location === 'not_reporting' || location === 'location_off' || location === 'no_reports';
  const fix = mayDraw && tech.lat != null && tech.lng != null
    ? { lat: Number(tech.lat), lng: Number(tech.lng), accuracyM: tech.accuracy_m ?? null, source: 'fix' as const, ageMinutes: fixAge }
    : null;

  let position: FleetPosition | null = null;
  let stale = false;
  if (fix && fixAge != null && fixAge <= FRESH_FIX_MINUTES) {
    position = fix;
  } else if (onSite && hasCoords(onSite) && fleet.tracking_enabled) {
    position = { lat: Number(onSite.lat), lng: Number(onSite.lng), accuracyM: null, source: 'job', ageMinutes: null };
  } else if (fix) {
    position = fix;
    stale = true;
  }

  let tone: FleetStatus['tone'];
  if (eta?.late || location === 'not_reporting' || location === 'location_off') tone = 'warning';
  else if (activity === 'on_site' || activity === 'en_route') tone = 'info';
  else if (location === 'live') tone = 'success';
  else tone = 'muted';

  return { activity, activityLabel, activeJob, nextJob, eta, location, locationLabel, tone, position, stale };
}

export interface NearbyTech<T> {
  item: T;
  km: number;
}

/** Closest technicians to a point, by straight-line distance (no road routing in v1). */
export function nearest<T>(
  target: { lat: number; lng: number },
  items: T[],
  positionOf: (item: T) => { lat: number; lng: number } | null,
  limit = 3,
): NearbyTech<T>[] {
  return items
    .map((item) => {
      const p = positionOf(item);
      return p ? { item, km: haversineKm(target, p) } : null;
    })
    .filter((x): x is NearbyTech<T> => x != null)
    .sort((a, b) => a.km - b.km)
    .slice(0, limit);
}

/** "Mon–Fri 07:00–18:00 · Sat 08:00–12:00", grouping consecutive days with the same window. */
export function hoursSummary(hours: TrackingHours | null | undefined): string {
  if (!hours) return 'No hours set';
  const parts: string[] = [];
  let i = 0;
  while (i < ISO_WEEKDAYS.length) {
    const w = hours[ISO_WEEKDAYS[i]];
    if (!w) { i++; continue; }
    let j = i;
    while (j + 1 < ISO_WEEKDAYS.length) {
      const n = hours[ISO_WEEKDAYS[j + 1]];
      if (!n || n[0] !== w[0] || n[1] !== w[1]) break;
      j++;
    }
    const days = i === j ? WEEKDAY_SHORT[ISO_WEEKDAYS[i]] : `${WEEKDAY_SHORT[ISO_WEEKDAYS[i]]}–${WEEKDAY_SHORT[ISO_WEEKDAYS[j]]}`;
    parts.push(`${days} ${w[0]}–${w[1]}`);
    i = j + 1;
  }
  return parts.length ? parts.join(' · ') : 'No tracking days';
}

const DAY_PREFIX: Record<string, IsoWeekday> = {
  mon: '1', tue: '2', wed: '3', thu: '4', fri: '5', sat: '6', sun: '7',
};

function parseClock(raw: string): string | null {
  const m = /^(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?$/i.exec(raw.trim());
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2] ?? '0');
  const ampm = m[3]?.toLowerCase();
  if (ampm === 'pm' && h < 12) h += 12;
  if (ampm === 'am' && h === 12) h = 0;
  if (h > 24 || min > 59 || (h === 24 && min > 0)) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/**
 * Best-effort read of the free-text companies.business_hours ("Mon-Fri: 8am-5pm, Sat: 9am-1pm")
 * to pre-fill the tracking hours. Null when nothing could be read; the admin confirms either way.
 */
export function parseBusinessHours(text: string | null | undefined): TrackingHours | null {
  if (!text) return null;
  const result: TrackingHours = { '1': null, '2': null, '3': null, '4': null, '5': null, '6': null, '7': null };
  let found = false;
  for (const segment of text.split(/[,;\n]+/)) {
    const m = /^\s*([a-z]{3})[a-z]*\.?\s*(?:(?:-|–|to)\s*([a-z]{3})[a-z]*\.?)?\s*:?\s*(.+?)\s*(?:-|–|to)\s*(.+?)\s*$/i.exec(segment);
    if (!m) continue;
    const from = DAY_PREFIX[m[1].toLowerCase()];
    const to = m[2] ? DAY_PREFIX[m[2].toLowerCase()] : from;
    const start = parseClock(m[3]);
    const end = parseClock(m[4]);
    if (!from || !to || !start || !end || start >= end) continue;
    for (let d = Number(from); d <= Number(to); d++) {
      result[String(d) as IsoWeekday] = [start, end];
      found = true;
    }
  }
  return found ? result : null;
}
